// src/components/FIRECalculator.js
import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import "./FIRECalculator.css";
import { MODE, MARKETING_LABEL } from "../config/compliance";
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";

Chart.register(...registerables);

// ===================== i18n helper ===================== //
function L(lang, it, en) { return lang === "en" ? en : it; }

// ===================== Currency config ===================== //
const CURRENCIES = {
  EUR: { symbol: "€", locale: "it-IT", defaultInflation: 2.0, name: "EUR (€)" },
  USD: { symbol: "$", locale: "en-US", defaultInflation: 2.5, name: "USD ($)" },
  GBP: { symbol: "£", locale: "en-GB", defaultInflation: 2.5, name: "GBP (£)" },
};

// ===================== MSCI World real annual returns 1970–2024 ===================== //
// Approximate USD real total returns (price + dividends - inflation)
// Sources: MSCI, Shiller CAPE data, World Bank CPI
const MSCI_REAL_ANNUAL = [
  // 1970–1979
  -0.035,  0.180,  0.277, -0.142, -0.275,
   0.369,  0.144, -0.035,  0.195,  0.120,
  // 1980–1989
   0.253, -0.048,  0.097,  0.225,  0.074,
   0.407,  0.418,  0.162,  0.233,  0.166,
  // 1990–1999
  -0.170,  0.183, -0.052,  0.225,  0.051,
   0.207,  0.135,  0.158,  0.243,  0.249,
  // 2000–2009
  -0.129, -0.165, -0.195,  0.331,  0.147,
   0.095,  0.201,  0.090, -0.407,  0.300,
  // 2010–2019
   0.118, -0.055,  0.158,  0.267,  0.049,
  -0.009,  0.075,  0.224, -0.087,  0.277,
  // 2020–2024
   0.159,  0.218, -0.181,  0.238,  0.170,
];

// ===================== Block Bootstrap Monte Carlo ===================== //
function blockBootstrapMC({ currentWealth, monthlyContrib, horizonYears, nSim = 2000, blockSize = 3 }) {
  const returns = MSCI_REAL_ANNUAL;
  const n = returns.length;
  const maxStart = n - blockSize;
  const allPaths = [];

  for (let s = 0; s < nSim; s++) {
    // Build annual return path by sampling contiguous blocks
    const annualPath = [];
    while (annualPath.length < horizonYears) {
      const start = Math.floor(Math.random() * (maxStart + 1));
      for (let b = 0; b < blockSize && annualPath.length < horizonYears; b++) {
        annualPath.push(returns[start + b]);
      }
    }

    // Simulate monthly wealth
    const yearlyWealth = [];
    let wealth = currentWealth;
    for (let y = 0; y < horizonYears; y++) {
      const monthlyR = Math.pow(1 + annualPath[y], 1 / 12) - 1;
      for (let m = 0; m < 12; m++) {
        wealth = wealth * (1 + monthlyR) + monthlyContrib;
        if (wealth < 0) wealth = 0;
      }
      yearlyWealth.push(wealth);
    }
    allPaths.push(yearlyWealth);
  }

  // Compute percentiles at each year
  const p5 = [], p25 = [], p50 = [], p75 = [], p95 = [];
  for (let y = 0; y < horizonYears; y++) {
    const col = allPaths.map(r => r[y]).sort((a, b) => a - b);
    const get = (p) => col[Math.max(0, Math.floor(col.length * p / 100))];
    p5.push(get(5));
    p25.push(get(25));
    p50.push(get(50));
    p75.push(get(75));
    p95.push(get(95));
  }

  return { percentiles: { p5, p25, p50, p75, p95 }, horizonYears, nSim };
}

// ===================== FIRE core math ===================== //
function calcFIRENumber(annualExpenses, swr) {
  return annualExpenses / (swr / 100);
}

function calcYearsToFire({ fireNumber, currentWealth, monthlyContrib, realReturnAnnual }) {
  if (currentWealth >= fireNumber) return 0;
  const r = Math.pow(1 + realReturnAnnual / 100, 1 / 12) - 1; // monthly real rate
  const A = monthlyContrib;
  const P = currentWealth;
  const FN = fireNumber;

  if (A <= 0 && r <= 0) return null; // impossible without return or growth
  if (r <= 0 && A > 0) return ((FN - P) / A) / 12; // pure linear accumulation

  // Standard FV formula: FN = P*(1+r)^n + A*[(1+r)^n - 1]/r
  // → (1+r)^n = (FN + A/r) / (P + A/r)
  if (A <= 0) {
    // Pure compounding, no contributions
    const arg = FN / P;
    if (arg <= 1) return null;
    return Math.log(arg) / Math.log(1 + r) / 12;
  }
  const arg = (FN + A / r) / (P + A / r);
  if (arg <= 1) return null;
  const months = Math.log(arg) / Math.log(1 + r);
  return months > 0 ? months / 12 : null;
}

// ===================== Expense wizard categories ===================== //
const EXPENSE_CATS = {
  housing:    { it: "Casa (affitto/mutuo)",  en: "Housing (rent/mortgage)" },
  food:       { it: "Cibo & spesa",          en: "Food & groceries" },
  transport:  { it: "Trasporti",             en: "Transport" },
  utilities:  { it: "Utenze",               en: "Utilities" },
  healthcare: { it: "Salute",               en: "Healthcare" },
  leisure:    { it: "Svago & ristorazione", en: "Leisure & dining" },
  clothing:   { it: "Abbigliamento",        en: "Clothing" },
  travel:     { it: "Viaggi (media/anno)",  en: "Travel (yearly avg)" },
  other:      { it: "Altro",               en: "Other" },
};

const FIRE_LABELS = (lang) => ({
  lean:    L(lang, "Lean FIRE (−30%)",  "Lean FIRE (−30%)"),
  regular: L(lang, "Regular FIRE",      "Regular FIRE"),
  fat:     L(lang, "Fat FIRE (+50%)",   "Fat FIRE (+50%)"),
  barista: L(lang, "Barista FIRE",      "Barista FIRE"),
  coast:   L(lang, "Coast FIRE",        "Coast FIRE"),
});

// ===================== Number formatter ===================== //
function fmt(value, currency) {
  const cfg = CURRENCIES[currency];
  return new Intl.NumberFormat(cfg.locale, { maximumFractionDigits: 0 }).format(value) + "\u202f" + cfg.symbol;
}

// ===================== Main component ===================== //
export default function FIRECalculator() {
  const { lang } = useContext(LangContext);
  const {
    trackToolStart,
    trackFireTypeSelected,
    trackWizardStart,
    trackWizardCompletion,
    trackMCSimulationRun,
    trackFIRECalculation,
    trackTimeOnTool,
  } = useAnalytics();

  // Traccia apertura tool e tempo di utilizzo al dismount
  const toolStartRef = useRef(Date.now());
  useEffect(() => {
    trackToolStart("fire_calculator", lang);
    toolStartRef.current = Date.now();
    return () => {
      const seconds = Math.round((Date.now() - toolStartRef.current) / 1000);
      trackTimeOnTool("fire_calculator", seconds, lang);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Inputs ---
  const [currency, setCurrency]         = useState("EUR");
  const [currentAge, setCurrentAge]     = useState(30);
  const [targetAge, setTargetAge]       = useState(50);
  const [currentWealth, setCurrentWealth] = useState(50000);
  const [monthlyContrib, setMonthlyContrib] = useState(1000);
  const [annualExpenses, setAnnualExpenses] = useState(24000);

  // --- Wizard ---
  const [showWizard, setShowWizard] = useState(false);
  const [wizardExp, setWizardExp] = useState(
    Object.fromEntries(Object.keys(EXPENSE_CATS).map(k => [k, ""]))
  );

  // --- FIRE type ---
  const [fireType, setFireType] = useState("regular");
  const [parttimeMonthly, setParttimeMonthly] = useState(800);

  // --- Advanced ---
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [swr, setSwr] = useState(3.5);
  const [realReturn, setRealReturn] = useState(5.0);

  // --- MC ---
  const [mcDone, setMcDone] = useState(false);
  const [mcResults, setMcResults] = useState(null);
  const [mcRunning, setMcRunning] = useState(false);

  // --- UI ---
  const [showAssumptions, setShowAssumptions] = useState(false);


  const sym = CURRENCIES[currency].symbol;
  const f   = (v) => fmt(v, currency);
  const labels = FIRE_LABELS(lang);

  // === Wizard total ===
  const wizardTotal = useMemo(() =>
    Object.values(wizardExp).reduce((s, v) => s + (parseFloat(v) || 0), 0) * 12,
    [wizardExp]
  );

  // === Effective expenses by FIRE type ===
  const effectiveExpenses = useMemo(() => {
    switch (fireType) {
      case "lean":    return annualExpenses * 0.70;
      case "fat":     return annualExpenses * 1.50;
      case "barista": return Math.max(0, annualExpenses - parttimeMonthly * 12);
      default:        return annualExpenses;
    }
  }, [annualExpenses, fireType, parttimeMonthly]);

  // === FIRE Number ===
  const fireNumber = useMemo(() => {
    if (fireType === "coast") {
      const fn = calcFIRENumber(annualExpenses, swr);
      const yearsLeft = Math.max(0, targetAge - currentAge);
      return yearsLeft > 0 ? fn / Math.pow(1 + realReturn / 100, yearsLeft) : fn;
    }
    return calcFIRENumber(effectiveExpenses, swr);
  }, [effectiveExpenses, annualExpenses, swr, fireType, realReturn, targetAge, currentAge]);

  // === Years to FIRE ===
  const yearsToFire = useMemo(() =>
    calcYearsToFire({ fireNumber, currentWealth, monthlyContrib, realReturnAnnual: realReturn }),
    [fireNumber, currentWealth, monthlyContrib, realReturn]
  );

  // === Savings rate (inferred income = expenses + savings) ===
  const savingsRate = useMemo(() => {
    const income = annualExpenses + monthlyContrib * 12;
    return income > 0 ? (monthlyContrib * 12) / income : null;
  }, [annualExpenses, monthlyContrib]);

  // === Analytics: traccia cambio tipo FIRE (skip prima render) ===
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    trackFireTypeSelected(fireType, lang);
  }, [fireType]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Analytics: traccia calcolo completato (debounced, solo quando risultati ready) ===
  useEffect(() => {
    if (!annualExpenses || annualExpenses <= 0) return;
    const timer = setTimeout(() => {
      trackFIRECalculation(fireType, yearsToFire, lang);
    }, 1500); // 1.5s debounce: evita eventi per ogni keystroke
    return () => clearTimeout(timer);
  }, [annualExpenses, currentWealth, monthlyContrib, swr, realReturn, fireType]); // eslint-disable-line react-hooks/exhaustive-deps

  // === All FIRE types for comparison table ===
  const allTypes = useMemo(() => {
    const coastFN = (() => {
      const fn = calcFIRENumber(annualExpenses, swr);
      const yl = Math.max(0, targetAge - currentAge);
      return yl > 0 ? fn / Math.pow(1 + realReturn / 100, yl) : fn;
    })();

    return {
      lean:    { exp: annualExpenses * 0.70, fn: calcFIRENumber(annualExpenses * 0.70, swr) },
      regular: { exp: annualExpenses,         fn: calcFIRENumber(annualExpenses, swr) },
      fat:     { exp: annualExpenses * 1.50, fn: calcFIRENumber(annualExpenses * 1.50, swr) },
      barista: { exp: Math.max(0, annualExpenses - parttimeMonthly * 12), fn: calcFIRENumber(Math.max(0, annualExpenses - parttimeMonthly * 12), swr) },
      coast:   { exp: annualExpenses, fn: coastFN },
    };
  }, [annualExpenses, swr, realReturn, targetAge, currentAge, parttimeMonthly]);

  const allTypesWithYears = useMemo(() => {
    return Object.fromEntries(
      Object.entries(allTypes).map(([type, data]) => [
        type,
        {
          ...data,
          years: calcYearsToFire({ fireNumber: data.fn, currentWealth, monthlyContrib, realReturnAnnual: realReturn }),
        },
      ])
    );
  }, [allTypes, currentWealth, monthlyContrib, realReturn]);

  // === Deterministic wealth path (50 years) ===
  const deterministicPath = useMemo(() => {
    const r = Math.pow(1 + realReturn / 100, 1 / 12) - 1;
    const path = [];
    let w = currentWealth;
    for (let m = 0; m < 50 * 12; m++) {
      w = w * (1 + r) + monthlyContrib;
      if (m % 12 === 11) path.push(w);
    }
    return path;
  }, [currentWealth, monthlyContrib, realReturn]);

  // === Chart: deterministic ===
  const detChartData = useMemo(() => ({
    labels: Array.from({ length: 50 }, (_, i) => String(currentAge + i + 1)),
    datasets: [
      {
        label: L(lang, "Patrimonio atteso", "Expected wealth"),
        data: deterministicPath,
        borderColor: "rgba(25,118,210,1)",
        backgroundColor: "rgba(25,118,210,0.07)",
        fill: true,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: L(lang, "FIRE Number", "FIRE Number"),
        data: Array(50).fill(fireNumber),
        borderColor: "rgba(239,68,68,0.85)",
        borderDash: [8, 4],
        fill: false,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  }), [deterministicPath, fireNumber, currentAge, lang]);

  // === Chart: Monte Carlo ===
  const mcChartData = useMemo(() => {
    if (!mcResults) return null;
    const { percentiles, horizonYears } = mcResults;
    const xLabels = Array.from({ length: horizonYears }, (_, i) => String(currentAge + i + 1));
    return {
      labels: xLabels,
      datasets: [
        { label: "5th %",    data: percentiles.p5,  borderColor: "rgba(239,68,68,0.8)",    fill: false,  pointRadius: 0, borderWidth: 1.5 },
        { label: "25th %",   data: percentiles.p25, borderColor: "rgba(251,146,60,0.8)",   fill: "+1", backgroundColor: "rgba(251,146,60,0.07)", pointRadius: 0, borderWidth: 1.5 },
        { label: L(lang, "Mediana", "Median"), data: percentiles.p50, borderColor: "rgba(25,118,210,1)", fill: false, pointRadius: 0, borderWidth: 2.5 },
        { label: "75th %",   data: percentiles.p75, borderColor: "rgba(34,197,94,0.8)",    fill: false,  pointRadius: 0, borderWidth: 1.5 },
        { label: "95th %",   data: percentiles.p95, borderColor: "rgba(34,197,94,0.9)",    fill: "-1", backgroundColor: "rgba(34,197,94,0.06)", pointRadius: 0, borderWidth: 1.5 },
      ],
    };
  }, [mcResults, currentAge, lang]);

  // === Run Monte Carlo (deferred to avoid blocking UI) ===
  const runMC = () => {
    setMcRunning(true);
    setTimeout(() => {
      const horizonYears = yearsToFire != null && yearsToFire > 0
        ? Math.min(Math.ceil(yearsToFire) + 10, 60)
        : 40;
      trackMCSimulationRun(horizonYears, lang);
      const res = blockBootstrapMC({ currentWealth, monthlyContrib, horizonYears, nSim: 2000, blockSize: 3 });
      setMcResults(res);
      setMcDone(true);
      setMcRunning(false);
    }, 30);
  };

  const isReady = annualExpenses > 0;

  return (
    <div className="fire-calc">

      {/* ── Header ── */}
      <div className="fire-header">
        <h2 className="fire-title">{L(lang, "Calcolatore F.I.R.E.", "F.I.R.E. Calculator")}</h2>
        <p className="fire-subtitle">
          {L(lang,
            "Stima il tuo percorso verso l'indipendenza finanziaria con simulazioni quantitative.",
            "Estimate your path to financial independence with quantitative simulations."
          )}
        </p>
      </div>

      {/* ── Input panel ── */}
      <div className="fire-panel">
        <h3 className="fire-section-title">{L(lang, "Parametri", "Parameters")}</h3>

        {/* Currency */}
        <div className="fire-field">
          <label className="fire-label">{L(lang, "Valuta", "Currency")}</label>
          <div className="fire-btn-group">
            {Object.entries(CURRENCIES).map(([k, v]) => (
              <button
                key={k}
                className={`fire-seg-btn${currency === k ? " active" : ""}`}
                onClick={() => setCurrency(k)}
              >
                {v.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ages */}
        <div className="fire-row">
          <div className="fire-field">
            <label className="fire-label">{L(lang, "Età attuale", "Current age")}</label>
            <input
              className="fire-input"
              type="number" min={18} max={80}
              value={currentAge}
              onChange={e => setCurrentAge(+e.target.value)}
            />
          </div>
          <div className="fire-field">
            <label className="fire-label">{L(lang, "Età target pensione", "Target retirement age")}</label>
            <input
              className="fire-input"
              type="number" min={currentAge + 1} max={100}
              value={targetAge}
              onChange={e => setTargetAge(+e.target.value)}
            />
          </div>
        </div>

        {/* Wealth & contributions */}
        <div className="fire-row">
          <div className="fire-field">
            <label className="fire-label">{L(lang, `Patrimonio attuale (${sym})`, `Current wealth (${sym})`)}</label>
            <input
              className="fire-input"
              type="number" min={0}
              value={currentWealth}
              onChange={e => setCurrentWealth(+e.target.value)}
            />
          </div>
          <div className="fire-field">
            <label className="fire-label">{L(lang, `Risparmio mensile (${sym})`, `Monthly savings (${sym})`)}</label>
            <input
              className="fire-input"
              type="number" min={0}
              value={monthlyContrib}
              onChange={e => setMonthlyContrib(+e.target.value)}
            />
          </div>
        </div>

        {/* Annual expenses + wizard */}
        <div className="fire-field">
          <div className="fire-expense-header">
            <label className="fire-label">{L(lang, `Spese annuali (${sym})`, `Annual expenses (${sym})`)}</label>
            <button className="fire-wizard-toggle" onClick={() => {
              if (!showWizard) trackWizardStart(lang);
              setShowWizard(!showWizard);
            }}>
              {showWizard
                ? L(lang, "← Inserimento diretto", "← Direct entry")
                : L(lang, "Stima le tue spese →", "Estimate your expenses →")}
            </button>
          </div>

          {!showWizard ? (
            <input
              className="fire-input"
              type="number" min={0}
              value={annualExpenses}
              onChange={e => setAnnualExpenses(+e.target.value)}
            />
          ) : (
            <div className="fire-wizard">
              <p className="fire-wizard-note">
                {L(lang, "Inserisci le spese mensili per categoria:", "Enter monthly expenses by category:")}
              </p>
              <div className="fire-wizard-grid">
                {Object.entries(EXPENSE_CATS).map(([k, lbl]) => (
                  <label key={k} className="fire-wizard-item">
                    <span className="fire-wizard-cat">{lbl[lang] || lbl.it}</span>
                    <input
                      className="fire-input fire-input-sm"
                      type="number" min={0} placeholder="0"
                      value={wizardExp[k]}
                      onChange={e => setWizardExp(prev => ({ ...prev, [k]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
              <div className="fire-wizard-footer">
                <span className="fire-wizard-total-label">
                  {L(lang, "Totale annuale:", "Annual total:")} <strong>{f(wizardTotal)}</strong>
                </span>
                <button
                  className="fire-apply-btn"
                  disabled={wizardTotal === 0}
                  onClick={() => {
                    trackWizardCompletion(wizardTotal, lang);
                    setAnnualExpenses(wizardTotal);
                    setShowWizard(false);
                  }}
                >
                  {L(lang, "Usa questo importo", "Use this amount")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FIRE type selector */}
        <div className="fire-field">
          <label className="fire-label">{L(lang, "Tipo di FIRE", "FIRE Type")}</label>
          <div className="fire-type-grid">
            {Object.entries(labels).map(([k, lbl]) => (
              <button
                key={k}
                className={`fire-type-btn${fireType === k ? " active" : ""}`}
                onClick={() => setFireType(k)}
              >
                {lbl}
              </button>
            ))}
          </div>
          <p className="fire-type-desc">
            {fireType === "lean"    && L(lang, "Stile frugale: 70% delle spese attuali.", "Frugal lifestyle: 70% of current expenses.")}
            {fireType === "regular" && L(lang, "Mantieni lo stile di vita attuale al 100%.", "Maintain your current lifestyle at 100%.")}
            {fireType === "fat"     && L(lang, "Stile premium: 150% delle spese attuali.", "Premium lifestyle: 150% of current expenses.")}
            {fireType === "barista" && L(lang, "Pensionamento parziale: un reddito part-time copre parte delle spese.", "Semi-retirement: part-time income covers part of expenses.")}
            {fireType === "coast"   && L(lang, "Accumula finché il patrimonio cresce autonomamente al FIRE Number entro l'età target.", "Accumulate until wealth grows on its own to the FIRE Number by target age.")}
          </p>
          {fireType === "barista" && (
            <div className="fire-field" style={{ marginTop: "0.75rem" }}>
              <label className="fire-label">{L(lang, `Reddito part-time mensile (${sym})`, `Monthly part-time income (${sym})`)}</label>
              <input
                className="fire-input"
                type="number" min={0}
                value={parttimeMonthly}
                onChange={e => setParttimeMonthly(+e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Advanced params accordion */}
        <button className="fire-advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
          <span>{L(lang, "Parametri avanzati", "Advanced parameters")}</span>
          <span className="fire-chevron">{showAdvanced ? "▲" : "▼"}</span>
        </button>
        {showAdvanced && (
          <div className="fire-advanced">
            <div className="fire-row">
              <div className="fire-field">
                <label className="fire-label">SWR — Safe Withdrawal Rate (%)</label>
                <input className="fire-input" type="number" min={2} max={6} step={0.1} value={swr} onChange={e => setSwr(+e.target.value)} />
                <p className="fire-hint">{L(lang, "Default 3.5% per orizzonti ≥ 40 anni (Estrada 2020)", "Default 3.5% for horizons ≥ 40 years (Estrada 2020)")}</p>
              </div>
              <div className="fire-field">
                <label className="fire-label">{L(lang, "Rendimento reale atteso (%/anno)", "Expected real return (%/year)")}</label>
                <input className="fire-input" type="number" min={0} max={15} step={0.1} value={realReturn} onChange={e => setRealReturn(+e.target.value)} />
                <p className="fire-hint">{L(lang, "Al netto dell'inflazione · default 5%", "Net of inflation · default 5%")}</p>
              </div>
            </div>
            {/* Revisione 29/09/2026: rimosso il campo "Inflazione prevista": non entrava in alcun
                calcolo (il modello lavora già in termini reali tramite il rendimento reale). */}
            <p className="fire-hint">
              {L(lang,
                "Tutti gli importi sono in termini reali (potere d'acquisto di oggi): il rendimento inserito è già al netto dell'inflazione.",
                "All amounts are in real terms (today's purchasing power): the return entered is already net of inflation.")}
            </p>
          </div>
        )}
      </div>

      {/* ── Results ── */}
      {isReady && (
        <div className="fire-results">
          <h3 className="fire-section-title">{L(lang, "Risultati", "Results")}</h3>

          {/* KPI cards */}
          <div className="fire-kpi-grid">
            <div className="fire-kpi">
              <span className="fire-kpi-label">
                {fireType === "coast" ? L(lang, "Coast FIRE Number", "Coast FIRE Number") : "FIRE Number"}
              </span>
              <span className="fire-kpi-value">{f(fireNumber)}</span>
              <span className="fire-kpi-sub">
                {fireType === "coast"
                  ? L(lang, `Patrimonio richiesto oggi per FI a ${targetAge} anni`, `Wealth needed today to FI at age ${targetAge}`)
                  : L(lang, `SWR ${swr}% · ${labels[fireType]}`, `SWR ${swr}% · ${labels[fireType]}`)}
              </span>
            </div>

            <div className="fire-kpi">
              <span className="fire-kpi-label">{L(lang, "Anni al FIRE", "Years to FIRE")}</span>
              <span className={`fire-kpi-value${yearsToFire === 0 ? " fire-kpi-achieved" : ""}`}>
                {yearsToFire === 0
                  ? L(lang, "Già raggiunto!", "Already reached!")
                  : yearsToFire != null
                  ? yearsToFire.toFixed(1)
                  : "—"}
              </span>
              <span className="fire-kpi-sub">
                {yearsToFire != null && yearsToFire > 0
                  ? L(lang, `Età prevista: ${(currentAge + yearsToFire).toFixed(0)} anni`, `Projected FIRE age: ${(currentAge + yearsToFire).toFixed(0)}`)
                  : yearsToFire === 0
                  ? L(lang, "Il patrimonio supera già il FIRE Number", "Wealth already exceeds FIRE Number")
                  : L(lang, "Aumenta il risparmio mensile", "Increase monthly savings")}
              </span>
            </div>

            <div className="fire-kpi">
              <span className="fire-kpi-label">{L(lang, "Tasso di risparmio", "Savings rate")}</span>
              <span className="fire-kpi-value">
                {savingsRate != null ? `${(savingsRate * 100).toFixed(1)}%` : "—"}
              </span>
              <span className="fire-kpi-sub">{L(lang, "Reddito stimato = spese + risparmio", "Estimated income = expenses + savings")}</span>
            </div>

            <div className="fire-kpi">
              <span className="fire-kpi-label">{L(lang, "Spese effettive/anno", "Effective expenses/year")}</span>
              <span className="fire-kpi-value">{f(effectiveExpenses)}</span>
              <span className="fire-kpi-sub">{L(lang, `Tipo: ${labels[fireType]}`, `Type: ${labels[fireType]}`)}</span>
            </div>
          </div>

          {/* Comparison table */}
          <div className="fire-comparison">
            <h4 className="fire-subsection-title">{L(lang, "Confronto tutti i tipi FIRE", "All FIRE types comparison")}</h4>
            <div className="fire-table-wrap">
              <table className="fire-table">
                <thead>
                  <tr>
                    <th>{L(lang, "Tipo", "Type")}</th>
                    <th>{L(lang, "Spese/anno", "Exp./year")}</th>
                    <th>{L(lang, "FIRE Number", "FIRE Number")}</th>
                    <th>{L(lang, "Anni", "Years")}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(allTypesWithYears).map(([type, data]) => (
                    <tr key={type} className={fireType === type ? "fire-table-active" : ""}>
                      <td>{labels[type]}</td>
                      <td>{f(data.exp)}</td>
                      <td>{f(data.fn)}</td>
                      <td>
                        {data.years === 0 ? "✓"
                          : data.years != null ? data.years.toFixed(1)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Deterministic chart */}
          <div className="fire-chart-block">
            <h4 className="fire-subsection-title">
              {L(lang, "Crescita del patrimonio — scenario deterministico", "Wealth growth — deterministic scenario")}
            </h4>
            <p className="fire-chart-note">
              {L(lang,
                `Rendimento reale ${realReturn}%, contributo mensile costante. La linea tratteggiata rossa è il FIRE Number.`,
                `Real return ${realReturn}%, constant monthly contribution. The dashed red line is the FIRE Number.`
              )}
            </p>
            <Line
              data={detChartData}
              options={{
                responsive: true,
                plugins: { legend: { position: "bottom" } },
                scales: {
                  x: { title: { display: true, text: L(lang, "Età", "Age") } },
                  y: { title: { display: true, text: L(lang, `Patrimonio (${sym})`, `Wealth (${sym})`) } },
                },
              }}
            />
          </div>

          {/* Monte Carlo */}
          <div className="fire-chart-block">
            <h4 className="fire-subsection-title">
              {L(lang, "Simulazione Monte Carlo — distribuzione dei possibili esiti", "Monte Carlo Simulation — distribution of possible outcomes")}
            </h4>
            <p className="fire-chart-note">
              {L(lang,
                "Block Bootstrap (blocchi da 3 anni, N = 2.000 sim.) su rendimenti reali annuali approssimati di un indice azionario mondiale (USD, 1970–2024). Ricampiona gli anni osservati: mostra l'effetto della sequenza dei rendimenti, ma non può generare eventi peggiori di quelli presenti nella serie (55 osservazioni).",
                "Block Bootstrap (3-year blocks, N = 2,000 sim.) on approximate annual real returns of a world equity index (USD, 1970–2024). It resamples observed years: it shows the effect of the sequence of returns, but cannot generate events worse than those in the series (55 observations)."
              )}
            </p>
            {!mcDone ? (
              <button className="fire-mc-btn" onClick={runMC} disabled={mcRunning}>
                {mcRunning
                  ? L(lang, "Simulazione in corso…", "Running simulation…")
                  : L(lang, "Esegui simulazione Monte Carlo", "Run Monte Carlo simulation")}
              </button>
            ) : (
              <>
                {mcChartData && (
                  <Line
                    data={mcChartData}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                      scales: {
                        x: { title: { display: true, text: L(lang, "Età", "Age") } },
                        y: { title: { display: true, text: L(lang, `Patrimonio (${sym})`, `Wealth (${sym})`) } },
                      },
                    }}
                  />
                )}
                <button className="fire-mc-reset" onClick={() => { setMcDone(false); setMcResults(null); }}>
                  {L(lang, "↺ Riesegui", "↺ Re-run")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Assumptions ── */}
      <div className="fire-assumptions-block">
        <button className="fire-assumptions-toggle" onClick={() => setShowAssumptions(!showAssumptions)}>
          {L(lang, "Assunzioni del modello", "Model assumptions")}
          <span className="fire-chevron">{showAssumptions ? " ▲" : " ▼"}</span>
        </button>
        {showAssumptions && (
          <div className="fire-assumptions-body">
            <ul>
              <li>{L(lang, `Rendimento reale annuo costante: ${realReturn}% (già al netto dell'inflazione; importi in potere d'acquisto di oggi)`, `Constant annual real return: ${realReturn}% (already net of inflation; amounts in today's purchasing power)`)}</li>
              <li>{L(lang, `Safe Withdrawal Rate: ${swr}% (Bengen 1994; Estrada 2020 per orizzonti ≥ 40 anni)`, `Safe Withdrawal Rate: ${swr}% (Bengen 1994; Estrada 2020 for horizons ≥ 40 years)`)}</li>
              <li>{L(lang, "Monte Carlo: Block Bootstrap (blocchi 3 anni) su una serie annuale approssimata di rendimenti reali in dollari USA di un indice azionario mondiale (MSCI World), 1970–2024 — non dati ufficiali; il rischio di cambio verso euro o sterlina non è modellato", "Monte Carlo: Block Bootstrap (3-year blocks) on an approximate annual series of real US-dollar returns of a world equity index (MSCI World), 1970–2024 — not official data; currency risk versus euro or pound is not modelled")}</li>
              <li>{L(lang, "I versamenti mensili sono ipotizzati costanti in termini reali", "Monthly contributions are assumed constant in real terms")}</li>
              <li>{L(lang, "Nessuna modellazione di imposte o costi di transazione (utilizzare SWR conservativo)", "No tax or transaction cost modeling (use a conservative SWR)")}</li>
              <li>{L(lang, `Coast FIRE: patrimonio necessario oggi perché cresca autonomamente al FIRE Number entro i ${targetAge} anni`, `Coast FIRE: wealth needed today to grow on its own to the FIRE Number by age ${targetAge}`)}</li>
            </ul>
            <p className="fire-ref">
              {L(lang,
                "Riferimenti: Bengen (1994); Trinity Study (1998); Pfau (2011); Estrada (2020); Karsten/ERN (2018–2024).",
                "References: Bengen (1994); Trinity Study (1998); Pfau (2011); Estrada (2020); Karsten/ERN (2018–2024)."
              )}
            </p>
          </div>
        )}
      </div>

      {/* ── Disclaimer ── */}
      <p className="fire-disclaimer">
        <small>
          {L(lang,
            "Le simulazioni sono a scopo illustrativo; non sono consulenza in materia di investimenti, raccomandazioni personalizzate né garanzia di risultati. L'accuratezza dei dati storici non è garantita; le decisioni d'investimento sono a esclusivo rischio dell'utente.",
            "Simulations are for illustrative purposes only; they are not investment advice, personalised recommendations nor a guarantee of results. The accuracy of historical data is not guaranteed; investment decisions are the sole responsibility of the user."
          )}
        </small>
      </p>
      {MODE !== "EDU" && (
        <p className="fire-marketing-note"><small>{MARKETING_LABEL}</small></p>
      )}
    </div>
  );
}
